import { LitElement, html, css, nothing } from 'lit';
import { customElement, state } from 'lit/decorators.js';

type Channel = 'email' | 'text';

interface MessageTemplate {
  id: number;
  channel: Channel;
  name: string;
  subject: string;
  body: string;
  is_default: boolean;
}

@customElement('fp3-templates')
export class Fp3Templates extends LitElement {
  @state() private channel: Channel = 'email';
  @state() private templates: MessageTemplate[] = [];
  @state() private selectedId = 0;
  @state() private name = '';
  @state() private subject = '';
  @state() private body = '';
  @state() private isDefault = false;
  @state() private error = '';
  @state() private notice = '';

  static styles = css`
    :host { display: block; }
    h2 { font-size: 1.2rem; font-weight: 600; margin: 0 0 0.3rem; letter-spacing: -0.02em; }
    .subtitle { color: var(--dash-muted, #8fa3b8); font-size: 0.9rem; line-height: 1.45; margin: 0 0 1rem; }
    .subtabs { display: flex; gap: 0.4rem; margin: 0 0 1rem; }
    button, input, textarea {
      font: inherit;
      color: var(--dash-text, #e8edf2);
      background: var(--dash-input-bg, #0f1419);
      border: 1px solid var(--dash-border, #314255);
      border-radius: 8px;
    }
    button { cursor: pointer; padding: 0.45rem 0.75rem; }
    button.primary { background: var(--dash-accent, #5b9fd4); color: #0f1419; border-color: var(--dash-accent, #5b9fd4); font-weight: 600; }
    button.quiet { background: transparent; }
    button[aria-pressed='true'] {
      background: var(--dash-accent, #5b9fd4);
      color: #0f1419;
      border-color: var(--dash-accent, #5b9fd4);
      font-weight: 600;
    }
    .layout {
      display: grid;
      grid-template-columns: minmax(180px, 240px) minmax(0, 1fr);
      gap: 0.85rem;
      align-items: start;
    }
    .list, .editor {
      border: 1px solid var(--dash-border, #314255);
      border-radius: 12px;
      background: var(--dash-surface, #121a22);
    }
    .list { display: grid; gap: 0.35rem; padding: 0.55rem; }
    .pick {
      display: flex;
      justify-content: space-between;
      align-items: center;
      gap: 0.4rem;
      width: 100%;
      text-align: left;
      background: transparent;
    }
    .pick.current { background: var(--dash-accent-soft, rgba(91, 159, 212, 0.16)); border-color: var(--dash-accent, #5b9fd4); }
    .mark {
      font-size: 0.65rem;
      letter-spacing: 0.04em;
      text-transform: uppercase;
      color: var(--dash-accent-2, #b7d7ee);
    }
    .editor { display: grid; gap: 0.7rem; padding: 1rem; }
    label {
      display: grid;
      gap: 0.3rem;
      font-size: 0.68rem;
      font-weight: 600;
      letter-spacing: 0.06em;
      text-transform: uppercase;
      color: var(--dash-muted, #8fa3b8);
    }
    input, textarea { padding: 0.55rem 0.7rem; width: 100%; box-sizing: border-box; }
    textarea { min-height: 220px; resize: vertical; line-height: 1.45; text-transform: none; letter-spacing: normal; font-weight: 400; }
    input:focus, textarea:focus { outline: 2px solid var(--dash-accent, #5b9fd4); outline-offset: 1px; }
    .check {
      text-transform: none;
      letter-spacing: normal;
      font-size: 0.88rem;
      font-weight: 500;
      color: var(--dash-text, #e8edf2);
      display: flex;
      gap: 0.45rem;
      align-items: center;
    }
    .actions { display: flex; gap: 0.5rem; flex-wrap: wrap; }
    .error { color: #f0a8a8; margin: 0; }
    .notice { color: var(--dash-good, #8fd0a8); margin: 0; }
    .tokens { color: var(--dash-faint, #6d8299); font-size: 0.8rem; margin: 0; }
    code { color: var(--dash-accent-2, #b7d7ee); }
    @media (max-width: 800px) { .layout { grid-template-columns: 1fr; } }
  `;

  connectedCallback() {
    super.connectedCallback();
    this.reload();
  }

  private async reload(keepId = this.selectedId) {
    this.error = '';
    const res = await fetch(`/api/templates?channel=${this.channel}`);
    if (!res.ok) {
      this.error = 'Templates are not available.';
      return;
    }
    const body = await res.json();
    this.templates = body.templates || [];
    const chosen = this.templates.find((item) => item.id === keepId)
      || this.templates.find((item) => item.is_default)
      || this.templates[0];
    this.apply(chosen);
  }

  private apply(template: MessageTemplate | undefined) {
    if (!template) {
      this.selectedId = 0;
      this.name = '';
      this.subject = '';
      this.body = '';
      this.isDefault = false;
      return;
    }
    this.selectedId = template.id;
    this.name = template.name;
    this.subject = template.subject;
    this.body = template.body;
    this.isDefault = template.is_default;
  }

  private async chooseChannel(channel: Channel) {
    if (this.channel === channel) return;
    this.channel = channel;
    this.notice = '';
    this.selectedId = 0;
    await this.reload(0);
  }

  private async save(event: Event) {
    event.preventDefault();
    if (!this.selectedId) return;
    this.error = '';
    this.notice = '';
    const res = await fetch(`/api/templates/${this.selectedId}`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        name: this.name,
        subject: this.channel === 'email' ? this.subject : '',
        body: this.body,
        is_default: this.isDefault,
      }),
    });
    if (!res.ok) {
      this.error = await readError(res);
      return;
    }
    this.notice = 'Saved. Nothing was sent.';
    await this.reload(this.selectedId);
  }

  private async addTemplate() {
    this.error = '';
    this.notice = '';
    const taken = new Set(this.templates.map((item) => item.name.toLowerCase()));
    let name = 'New template';
    let n = 2;
    while (taken.has(name.toLowerCase())) {
      name = `New template ${n}`;
      n += 1;
    }
    const res = await fetch('/api/templates', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        channel: this.channel,
        name,
        subject: this.channel === 'email' ? 'FlightPath3D for {tail}' : '',
        body: 'Hello {name},\n\n',
        is_default: false,
      }),
    });
    if (!res.ok) {
      this.error = await readError(res);
      return;
    }
    const created = await res.json();
    await this.reload(created.id);
  }

  private async removeTemplate() {
    if (!this.selectedId) return;
    this.error = '';
    this.notice = '';
    const res = await fetch(`/api/templates/${this.selectedId}`, { method: 'DELETE' });
    if (!res.ok) {
      this.error = await readError(res);
      return;
    }
    this.selectedId = 0;
    await this.reload(0);
  }

  render() {
    const selected = this.templates.find((item) => item.id === this.selectedId);
    return html`
      <p class="subtitle">Write the email or text once, then save it. Previews use the template marked for preview. Nothing is sent from here.</p>
      <div class="subtabs" role="tablist" aria-label="Template kind">
        <button type="button" role="tab" aria-pressed=${this.channel === 'email'} @click=${() => this.chooseChannel('email')}>Email templates</button>
        <button type="button" role="tab" aria-pressed=${this.channel === 'text'} @click=${() => this.chooseChannel('text')}>Text templates</button>
      </div>
      ${this.error ? html`<p class="error">${this.error}</p>` : nothing}
      ${this.notice ? html`<p class="notice">${this.notice}</p>` : nothing}
      <div class="layout">
        <div class="list">
          ${this.templates.map((item) => html`
            <button type="button" class="pick ${item.id === this.selectedId ? 'current' : ''}" @click=${() => { this.notice = ''; this.apply(item); }}>
              <span>${item.name}</span>
              ${item.is_default ? html`<span class="mark">Preview</span>` : nothing}
            </button>
          `)}
          <button type="button" class="quiet" @click=${this.addTemplate}>Add template</button>
        </div>
        ${selected
          ? html`
            <form class="editor" @submit=${this.save}>
              <label>Name
                <input required maxlength="80" .value=${this.name} @input=${(event: Event) => { this.name = (event.target as HTMLInputElement).value; }} />
              </label>
              ${this.channel === 'email'
                ? html`<label>Subject
                    <input required maxlength="200" .value=${this.subject} @input=${(event: Event) => { this.subject = (event.target as HTMLInputElement).value; }} />
                  </label>`
                : nothing}
              <label>Message
                <textarea required maxlength="4000" .value=${this.body} @input=${(event: Event) => { this.body = (event.target as HTMLTextAreaElement).value; }}></textarea>
              </label>
              <p class="tokens">Tokens: <code>{name}</code> <code>{tail}</code> <code>{family}</code></p>
              <label class="check">
                <input type="checkbox" .checked=${this.isDefault} @change=${(event: Event) => { this.isDefault = (event.target as HTMLInputElement).checked; }} />
                Use this one in previews
              </label>
              <div class="actions">
                <button class="primary" type="submit">Save</button>
                <button type="button" class="quiet" @click=${this.removeTemplate}>Delete</button>
              </div>
            </form>
          `
          : html`<div class="editor"><p class="subtitle">No template is selected.</p></div>`}
      </div>
    `;
  }
}

async function readError(res: Response) {
  try {
    const body = await res.json();
    return typeof body.detail === 'string' ? body.detail : 'Could not save that template.';
  } catch {
    return 'Could not save that template.';
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'fp3-templates': Fp3Templates;
  }
}
